import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, Typography, CircularProgress, TextField, Button } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReLoginModal from '../../ReLoginModal';

interface AllocationModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (allocationData: Allocation) => void;
}

interface Allocation {
    _id: string;
    customer: { _id: string; businessName: string };
    license: {
        _id: string;
        licenseOwner: string;
        licenseId: string;
        licenseNumber: string;
    };
    destination: string;
    exDepotPrice: string;
    product?: { id: string; name: string; selling: number };
    programId: string;
    truckNumber: string;
    quantity?: string;
}

type ApiError = {
    response?: {
        data?: {
            message?: string;
        };
    };
    message?: string;
};

const AllocationSelectModal: React.FC<AllocationModalProps> = ({ open, onClose, onSelect }) => {
    const [allocations, setAllocations] = useState<Allocation[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reLogin, setReLogin] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState<string | null>(null);

    const fetchAllocations = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        if (!token) {
            setReLogin(true);
            toast.error('Session expired, please log in again.');
            setIsLoading(false);
            return;
        }

        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
        try {
            const response = await axios.get('https://apibiz.oneflaretech.com/api/daily-allocations', { headers });
            // console.log('Fetched allocations:', response.data.data.dailyAllocations);
            setAllocations(response.data.data.dailyAllocations || []);
        } catch (error) {
            const err = error as ApiError;
            const errorMessage = err.response?.data?.message || 'Failed to load Daily Allocations';
            setError(errorMessage);
            if (errorMessage.includes('jwt expired') || errorMessage.includes('jwt malformed')) {
                setReLogin(true);
                toast.error('Session expired, please log in again.');
            } else {
                toast.error(errorMessage);
            }
            console.error('Error fetching licenses:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            fetchAllocations();
        }
    }, [open, fetchAllocations]);

    const handleSelectAllocation = (allocation: Allocation) => {
        if (!allocation.license.licenseOwner) {
            toast.error('Selected allocation has no associated customer.');
            return;
        }
        onSelect({
            _id: allocation._id,
            customer: allocation.customer,
            license: allocation.license,
            destination: allocation.destination,
            exDepotPrice: allocation.exDepotPrice,
            product: allocation.product,
            programId: allocation.programId,
            truckNumber: allocation.truckNumber,
            quantity: allocation.quantity,
        });
        onClose();
    };

    const filteredAllocations = allocations.filter(allocation =>
        allocation.customer.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        allocation.license.licenseOwner.toLowerCase().includes(searchQuery.toLowerCase())
        
    );

    return (
        <>
            {reLogin && <ReLoginModal />}
            <Modal open={open} onClose={onClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'white',
                        boxShadow: 24,
                        p: 4,
                        minWidth: 400,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                    }}
                >
                    <Typography variant="h6" component="h2" gutterBottom>
                        Select Allocation
                    </Typography>
                    <TextField
                        label="Search by License Owner or Customer"
                        variant="outlined"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ mb: 2, maxWidth: '250px' }}
                        InputProps={{
                            endAdornment: <Button onClick={() => { }} sx={{ minWidth: 'auto', color: 'black' }}>🔍</Button>,
                        }}
                    />
                    {isLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            <CircularProgress />
                        </div>
                    ) : error ? (
                        <Typography color="error" align="center">{error}</Typography>
                    ) : filteredAllocations.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid black' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5', fontWeight: 'bold', borderBottom: '1px solid black' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>License owner</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>Customer name</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>Product</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'black' }}>Truck Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAllocations.map((allocation) => (
                                    <tr
                                        key={allocation._id}
                                        className='hover:bg-blue-100'
                                        style={{ height: '40px', borderBottom: '1px solid black', cursor: 'pointer' }}
                                        onClick={() => handleSelectAllocation(allocation)}
                                    >
                                        <td style={{ padding: '4px', color: 'black' }}>{allocation.license.licenseOwner}</td>
                                        <td style={{ padding: '4px', color: 'black' }}>{allocation.customer.businessName || 'N/A'}</td>
                                        <td style={{ padding: '4px', color: 'black' }}>{allocation.product?.name || 'N/A'}</td>
                                        <td style={{ padding: '4px', color: 'black' }}>{allocation.truckNumber || 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <Typography align="center">No Daily Allocations Available</Typography>
                    )}
                    <Button variant="contained" color="primary" onClick={onClose} sx={{ mt: 2 }}>
                        Cancel
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default AllocationSelectModal;